import React, { useState, useRef } from 'react';
import { get } from 'lodash';
import { compose } from '@xo-union/react-css-modules';
import { ToggleSwitch } from '@xo-union/tk-component-switches';
import { Body1, Subhead } from '@xo-union/tk-ui-typography';
import GdsClient, { EventResponse } from '../../gds-api-client';
import { ActionItem } from '../../gs-component-form';
import { DisplayButton } from '@xo-union/tk-ui-links';
import Modal, { ModalHeader } from '@xo-union/tk-component-modals';
import { Snackbar } from '@xo-union/tk-component-alerts';
import { Button } from '@xo-union/tk-component-buttons';

import { EventType } from '../EventTypes';
import { getAnalyticsProperties, track } from '../helpers/analytics';
import { spaceBarPressed } from '../helpers/utilities';
import { emptyQuestion } from '../helpers/question';
import styles from './eventModule.scss';
import QuestionModule from './QuestionModule';
import NoQuestionsModule from './NoQuestionsModule';
import { GuestWeddingProperty } from '.';

interface Props {
  displayRsvpToggle: boolean;
  event: EventType;
  privateRsvp: boolean;
  rsvpOnCount: number;
  numOfRsvpAbleEvents: number;
  onEventsUpdate: (events: EventResponse[], variant?: string) => void;
  goToGuestList: (id: string) => void;
  openQuestionRail: (question: any, eventType: any) => void;
  gdsClient: GdsClient;
  numOfAnswers: number | null;
  updateGuestWedding: (property: GuestWeddingProperty, newValue: boolean) => void;
  rootTestId: string | number;
}

export interface SnackbarInterface {
  showAndDismiss: (ops: any) => void;
}

interface RsvpConfirmationModalProps {
  onClose: () => void;
  onSuccess: () => void;
  numOfRsvpAbleEvents: number;
}

const RsvpConfirmationModal: React.FC<RsvpConfirmationModalProps> = ({ onClose, onSuccess, numOfRsvpAbleEvents }) => {
  const note =
    numOfRsvpAbleEvents === 1
      ? 'Since this is the only event you are collecting RSVPs for, this page will be automatically hidden on your Website.'
      : "If you are no longer collecting RSVPs for any of your events, you can hide the entire page in 'Settings'.";

  return (
    <Modal size="sm" onClose={onClose}>
      <ModalHeader className={styles.modalHeader}>Are you sure?</ModalHeader>
      <Body1 className={styles.topMessage}>
        Guests will no longer be able RSVP for this event or answer your follow-up questions. All previous RSVPs and
        answers will be saved.
      </Body1>
      <Body1 className={styles.bottomNote}>
        <b>Note:</b> <span>{note}</span>
      </Body1>
      <div className={styles.modalButtonFooter}>
        <Button color="secondary" size="lg" onClick={onClose}>
          No, Cancel
        </Button>
        <Button color="primary" size="lg" data-testid="yes-continue-button" onClick={onSuccess}>
          Yes, Continue
        </Button>
      </div>
    </Modal>
  );
};
export const DIMISS_TIMEOUT = 2500;

const EventModule: React.FC<Props> = ({
  displayRsvpToggle,
  event,
  goToGuestList,
  privateRsvp,
  rsvpOnCount,
  onEventsUpdate,
  openQuestionRail,
  gdsClient,
  numOfRsvpAbleEvents,
  numOfAnswers,
  updateGuestWedding,
  rootTestId,
}: Props) => {
  const [isRsvpRequired, setIsRsvpRequired] = useState(event.requireRsvp || false);
  const [openRsvpConfirmationModal, setRsvpConfirmationModal] = useState(false);
  const snackbar = useRef<SnackbarInterface>();

  const analyticsProperties = {
    ...getAnalyticsProperties(event, privateRsvp, rsvpOnCount),
  };

  const setRsvpOnEvent = async (id: string, requireRsvp: boolean): Promise<void> => {
    try {
      const response = await gdsClient.patchEvent(id, { requireRsvp });
      onEventsUpdate([response.data]);
      if (snackbar && snackbar.current) {
        snackbar.current.showAndDismiss({
          data: `Got it! We've updated the RSVP for '${event.name}'.`,
          dismissTimeout: DIMISS_TIMEOUT,
        });
      }
    } catch ({ response }) {
      if (snackbar && snackbar.current && get(response, ['data', 'error'])) {
        snackbar.current.showAndDismiss({
          data: 'Oops, something went wrong!',
          dismissTimeout: DIMISS_TIMEOUT,
        });
      }
    }
  };

  const updateRequireRsvp = (checked: boolean): void => {
    setIsRsvpRequired(checked);
    analyticsProperties.rsvpOnCount = checked ? rsvpOnCount + 1 : rsvpOnCount - 1 || 0;

    track('RSVP Settings Interaction', {
      ...analyticsProperties,
      action: checked ? 'enable rsvp' : 'disable rsvp',
      rsvpOn: checked,
    });
    setRsvpOnEvent(event.id, checked);
  };

  const onRequireRsvpChange = async ({ target }: any): Promise<void> => {
    if (!target.checked && (numOfRsvpAbleEvents === 1 || numOfAnswers)) {
      setRsvpConfirmationModal(true);
    } else {
      updateRequireRsvp(target.checked);
    }
  };

  const openEvent = (e: React.MouseEvent): void => {
    e.preventDefault();
    goToGuestList(event.id);
  };

  let questions;
  if (!event.questions || event.questions.length === 0) {
    questions = (
      <NoQuestionsModule
        privateRsvp={privateRsvp}
        event={event}
        openEvent={openEvent}
        openQuestionRightRail={openQuestionRail}
        isRsvpRequired={isRsvpRequired}
        testId={`event-${rootTestId}`}
      />
    );
  } else {
    questions = (
      <>
        {event.questions.map((q, idx) => (
          <QuestionModule
            key={`event-${event.id}-question-${q.id}`}
            testId={`${rootTestId}-${idx}`}
            editQuestion={(): void => openQuestionRail(q, event)}
            question={q}
            isGeneralQuestion={false}
            disabledQuestionUI={!isRsvpRequired}
          />
        ))}
        <div data-testid="addQuestionCTA" className={styles.addQuestionContainer}>
          <ActionItem type="add" onClick={(): void => openQuestionRail(emptyQuestion, event)}>
            <div data-testid={`askFollowUp-${rootTestId}`}>Add a Follow-Up Question</div>
          </ActionItem>
        </div>
      </>
    );
  }

  // Only show the guest count description if there are questions on the event.
  let guestDescription;
  if (event.questions && event.questions.length && !privateRsvp) {
    // If RSVP is public, show a different description
    guestDescription = (
      <span className={styles.guestDescription}>
        Questions will be asked to anyone who RSVPs &apos;Yes&apos; to this event on your Website.
      </span>
    );
  } else if (event.guestCount === 0 && event.questions && event.questions.length) {
    // If there are no guests on this event, let them know.
    guestDescription = (
      <span data-testid="guestDescriptionNoGuests" className={styles.guestDescription}>
        Heads up! There are <u>0 guests</u> on your{' '}
        <DisplayButton data-testid="guestDescriptionEventLink" onClick={openEvent} className={styles.displayLink}>
          {event.name}
        </DisplayButton>{' '}
        guest list.
      </span>
    );
  } else if (get(event, ['questions', 'length'])) {
    // Show the number of guests who will be asked this question.
    const guestCount = get(event, ['guestCount']);
    guestDescription = (
      <span data-testid="guestDescription" className={styles.guestDescription}>
        {guestCount && guestCount > 1
          ? `Questions will be asked to all of the ${guestCount} guests on the `
          : `Questions will be asked to the guest on your `}
        <DisplayButton
          data-testid="guestDescriptionEventLink"
          onClick={openEvent}
          onKeyDown={(e: any): void | boolean => spaceBarPressed(e) && openEvent(e)}
          className={styles.displayLink}
          tabIndex={0}
        >
          {event.name}
        </DisplayButton>
        {' list '}
        who RSVP &apos;Yes&apos;
      </span>
    );
  }

  return (
    <>
      <Snackbar ref={snackbar} />
      <div className={styles.eventModuleContainer}>
        <div className={styles.eventTitleContainer}>
          <Subhead>
            <strong>{event.name}</strong>
          </Subhead>
          {displayRsvpToggle && (
            <ToggleSwitch
              classes={compose({ container: styles.toggleSwitchContainer })}
              checked={isRsvpRequired}
              onChange={onRequireRsvpChange}
              labelDirection="left"
              data-testid="collect-rsvp-toggle"
            >
              Collect RSVPs
            </ToggleSwitch>
          )}
        </div>
        {guestDescription}
        {questions}
        {openRsvpConfirmationModal && (
          <RsvpConfirmationModal
            numOfRsvpAbleEvents={numOfRsvpAbleEvents}
            onClose={(): void => setRsvpConfirmationModal(false)}
            onSuccess={(): void => {
              updateRequireRsvp(!isRsvpRequired);
              setRsvpConfirmationModal(false);
              if (numOfRsvpAbleEvents === 1) {
                updateGuestWedding('rsvpPageHidden', true);
              }
            }}
          />
        )}
      </div>
    </>
  );
};
export default EventModule;
